import React, { useEffect, useState } from 'react';

import MicroTour from './MicroTour';
import { sectionTypeTours } from './tours';

/**
 * Auto-fires a per-type spotlight tour the first time the user lands on a
 * section editor of that type in the session. Re-mounts when the type
 * changes (via the URL) — distinct from the main onboarding walkthrough,
 * which is keyed by `?onboarding=<step>`.
 */
interface SectionTypeTourProps {
  type: SectionType;
}

const SectionTypeTour: React.FC<SectionTypeTourProps> = ({ type }) => {
  const [active, setActive] = useState(false);

  // The per-type tour no longer auto-fires on first arrival — it was too
  // intrusive. It now only runs when the user explicitly asks for it via the
  // "Tour this type" button in the section topbar, which dispatches the
  // replay event below.
  useEffect(() => {
    if (!sectionTypeTours[type]) return;
    const onReplay = () => setActive(true);

    window.addEventListener('onark:section-tour-replay', onReplay);

    return () => {
      window.removeEventListener('onark:section-tour-replay', onReplay);
    };
  }, [type]);

  // Reset when navigating between section types so a stale tour doesn't
  // carry over to a different editor.
  useEffect(() => {
    setActive(false);
  }, [type]);

  if (!active) return null;
  const steps = sectionTypeTours[type];

  if (!steps) return null;

  return <MicroTour steps={steps} onClose={() => setActive(false)} />;
};

export default SectionTypeTour;

/**
 * Imperative launcher used by the "Tour this section type" button in the
 * topbar. The tour is now opt-in only, so this simply tells any mounted
 * SectionTypeTour to start.
 */
export function replaySectionTypeTour(_type: SectionType) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('onark:section-tour-replay'));
}
